import React, { useEffect, useRef, useState } from "react";
import { DragDropContext } from "react-beautiful-dnd";
import { MdRefresh, MdScreenRotation } from "react-icons/md";
import { BsChevronLeft, BsFillGearFill } from "react-icons/bs";
import { VscDebugRestart } from "react-icons/vsc";
import { Modal as ResponsiveModal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";

import {
  AppLogo,
  ButtonVoltar,
  Container,
  FloatingRestartButton,
  Rotate,
} from "./styles";
import { gridStyles } from "./globalStyles/gridStyles";
import Controls from "./components/Controls";
import SpareNumbersContainer from "./components/SpareNumbersContainer";
import NumberSlotsContainer from "./components/NumberSlotsContainer";
import Modal from "./components/Modal";
import dragEndController from "./controllers/dragEndController";
import dragStartController from "./controllers/dragStartController";
import ModalForm from "./components/ModalForm";
import Guide from "./components/Guide";
import logoTexto from "./assets/logoTextoTabuleiro.png";
import History from "./components/History";

function App() {
  const DEFAULT_SLOT_AMOUNT = 50;

  const [showGuide, setShowGuide] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [guideMode, setGuideMode] = useState("short");
  const [slots, setSlots] = useState([]);
  const [spareNumbers, setSpareNumbers] = useState([]);
  const [unlistedNumbers, setUnlistedNumbers] = useState([]);
  const [showCorrectNumbers, setShowCorrectNumbers] = useState(false);
  const [showMistakes, setShowMistakes] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [allSlotsCorrect, setAllSlotsCorrect] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [resultSent, setResultSent] = useState(false);
  const [logoVisible, setLogoVisible] = useState(true);
  const [timesHistory, setTimesHistory] = useState([]);

  const [startTime, setStartTime] = useState();
  const [lastTime, setLastTime] = useState();

  const logoRef = useRef(false);
  const slotsRef = useRef(false);

  useEffect(() => {
    let strSavedSize = localStorage.getItem("lastSize");
    let savedSize = null;
    try {
      savedSize = JSON.parse(strSavedSize);
    } catch (error) {
      localStorage.setItem("lastSize", JSON.stringify(DEFAULT_SLOT_AMOUNT));
    }

    let slotAmount;
    savedSize ? (slotAmount = savedSize) : (slotAmount = DEFAULT_SLOT_AMOUNT);

    let defaultSlots = [];
    let defaultUnlistedNumbers = [];
    for (let index = 0; index < slotAmount; index++) {
      defaultSlots.push(null);
      defaultUnlistedNumbers.push(index);
    }
    setSlots(defaultSlots);
    let randomNumber = defaultUnlistedNumbers.splice(
      parseInt(Math.random() * defaultUnlistedNumbers.length),
      1
    )[0];
    setUnlistedNumbers(defaultUnlistedNumbers);
    setSpareNumbers([randomNumber]);
    getStoredHistory();

    let showCorrectNumbersLastState = JSON.parse(
      localStorage.getItem("showCorrectNumbersLastState")
    );
    if (showCorrectNumbersLastState) setShowCorrectNumbers(true);

    let showMistakesLastState = JSON.parse(
      localStorage.getItem("showMistakesLastState")
    );
    if (showMistakesLastState) setShowMistakes(true);

    window.addEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    handleResize();
  }, [slots]);

  function handleResize() {
    if (!slotsRef.current && slotsRef.current.clientHeight < 60) return;

    let windowHeight = window.innerHeight;
    let slotsHeight = slotsRef.current.clientHeight;
    let logoHeight = 80;

    if (windowHeight > slotsHeight + logoHeight * 2) {
      setLogoVisible(true);
      slotsRef.current.style.marginTop = 0;
      return;
    }

    if (windowHeight > slotsHeight + logoHeight) {
      setLogoVisible(true);
      slotsRef.current.style.marginTop = "100px";
      return;
    }

    setLogoVisible(false);
    slotsRef.current.style.marginTop = 0;
  }

  function generateRandomNumber() {
    if (unlistedNumbers.length > 0) {
      let updatedUnlistedNumbers = [...unlistedNumbers];
      let randomNumber = updatedUnlistedNumbers.splice(
        parseInt(Math.random() * updatedUnlistedNumbers.length),
        1
      )[0];
      setUnlistedNumbers([...updatedUnlistedNumbers]);
      setSpareNumbers([randomNumber]);
    }
  }

  function clearGrid(size) {
    setResultSent(false);

    let slotAmount;
    if (size) {
      slotAmount = size;
    } else {
      slotAmount = slots.length;
    }

    let updatedSlots = [];
    let updatedUnlistedNumbers = [];
    for (let index = 0; index < slotAmount; index++) {
      updatedSlots.push(null);
      updatedUnlistedNumbers.push(index);
    }
    let randomNumber = updatedUnlistedNumbers.splice(
      parseInt(Math.random() * updatedUnlistedNumbers.length),
      1
    )[0];
    setSlots(updatedSlots);
    setUnlistedNumbers([...updatedUnlistedNumbers]);
    setSpareNumbers([randomNumber]);
    setStartTime(null);
  }

  function formatTime(time) {
    let fixedTime = Number(time).toFixed();
    let minutes = Math.floor(fixedTime / 60);
    if (minutes > 0) {
      fixedTime = fixedTime - minutes * 60;
    }
    if (fixedTime < 10) fixedTime = `0${fixedTime}`;
    return `${minutes}:${fixedTime}`;
  }

  function getStoredHistory() {
    let storedHistory = localStorage.getItem("timesHistory");
    if (!storedHistory) return;

    let parsedTimesHistory = JSON.parse(storedHistory);

    if (!parsedTimesHistory || !Array.isArray(parsedTimesHistory)) {
      localStorage.setItem("timesHistory", JSON.stringify([]));
      return;
    }

    setTimesHistory(parsedTimesHistory);
  }

  function updateHistory() {
    let endTimestamp = new Date();
    let timeInSeconds = Math.floor((endTimestamp - startTime) / 1000);
    setLastTime(timeInSeconds);

    //                    *deep copy
    let newTimesHistory = timesHistory.map(item =>
      Array.isArray(item) ? [...item] : item
    );

    if (Array.isArray(newTimesHistory[slots.length / 10])) {
      newTimesHistory[slots.length / 10].push(timeInSeconds);
    } else {
      newTimesHistory[slots.length / 10] = [timeInSeconds];
    }

    setTimesHistory(newTimesHistory);
    localStorage.setItem("timesHistory", JSON.stringify(newTimesHistory));
  }

  function clearHistory() {
    let newTimesHistory = [];

    setTimesHistory(newTimesHistory);
    localStorage.setItem("timesHistory", JSON.stringify(newTimesHistory));
  }

  return (
    <>
      <Rotate>
        <div className="icon">
          <MdScreenRotation />
        </div>
        <div className="labels">
          <span>Use seu dispositivo na horizontal</span>
          <span>(Verifique se a rotação está ativada)</span>
        </div>
      </Rotate>
      <Container largeScreen={window.screen.availWidth > 1400}>
        <ResponsiveModal open={showGuide} onClose={() => setShowGuide(false)}>
          <Guide
            mode={guideMode}
            setMode={setGuideMode}
            hide={() => setShowGuide(false)}
          />
        </ResponsiveModal>
        <History
          open={showHistory}
          onClose={() => setShowHistory(false)}
          data={timesHistory}
          currentSize={slots.length / 10}
          clearHistory={() => clearHistory()}
        />
        <Controls
          currentSize={slots.length / 10}
          clearGrid={size => {
            clearGrid(size);
            if (size && typeof size == "number") {
              localStorage.setItem("lastSize", size);
            }
          }}
          showCorrectNumbers={showCorrectNumbers}
          setShowCorrectNumbers={value => {
            setShowCorrectNumbers(value);
            localStorage.setItem(
              "showCorrectNumbersLastState",
              JSON.stringify(!!value)
            );
          }}
          showMistakes={showMistakes}
          setShowMistakes={value => {
            setShowMistakes(value);
            localStorage.setItem(
              "showMistakesLastState",
              JSON.stringify(!!value)
            );
          }}
          expanded={showOptions}
          setExpanded={setShowOptions}
          time={lastTime && formatTime(lastTime)}
          showGuide={() => {
            setGuideMode("short");
            setShowGuide(true);
          }}
          showTimes={() => setShowHistory(true)}
        />
        <FloatingRestartButton onClick={() => clearGrid(null)}>
          <VscDebugRestart />
          <span>Reiniciar</span>
        </FloatingRestartButton>
        <Modal visible={allSlotsCorrect} hide={() => setAllSlotsCorrect(false)}>
          <div className="modal-content">
            <h3>Atividade completa!</h3>
            <h4>Tempo: {formatTime(lastTime)}</h4>
            <div className="form">
              <ModalForm
                time={formatTime(lastTime)}
                size={slots.length}
                resultSent={resultSent}
                setResultSent={setResultSent}
              />
            </div>
            <div className="buttons">
              <button
                className="restart"
                onClick={() => {
                  clearGrid();
                  setAllSlotsCorrect(false);
                }}
              >
                <MdRefresh />
                <span>Reiniciar</span>
              </button>
              <button
                className="options"
                onClick={() => {
                  setShowOptions(true);
                  setAllSlotsCorrect(false);
                }}
              >
                <BsFillGearFill />
                <span>Opções</span>
              </button>
            </div>
          </div>
        </Modal>
        <div className="application-container">
          <AppLogo
            src={logoTexto}
            alt=""
            srcSet=""
            visible={logoVisible}
            ref={logoRef}
            onClick={() => {
              setGuideMode("full");
              setShowGuide(true);
            }}
          />
          <ButtonVoltar href="https://metodoliquen.com.br">
            <BsChevronLeft />
            Voltar
          </ButtonVoltar>
          <div className="application">
            <DragDropContext
              onDragStart={event => {
                dragStartController(event, setDragging);
                !startTime && setStartTime(Date.now());
              }}
              onDragEnd={event => {
                setDragging(null);
                dragEndController(event, {
                  slots: slots,
                  setSlots: setSlots,
                  spareNumbers: spareNumbers,
                  setSpareNumbers: setSpareNumbers,
                  generateRandomNumber: generateRandomNumber,
                  validateSlots: unlistedNumbers.length === 0,
                  onAllSlotsCorrect: () => {
                    setAllSlotsCorrect(true);
                    updateHistory();
                  },
                });
              }}
            >
              <NumberSlotsContainer
                elementRef={slotsRef}
                slots={slots}
                showCorrectNumbers={showCorrectNumbers}
                showMistakes={showMistakes}
                dragging={dragging}
                className="slots"
                smallLayout={
                  window.innerHeight <
                  (slots.length / 10) * gridStyles.default.slotSize +
                    gridStyles.default.gridPadding * 2
                }
              />
              <SpareNumbersContainer
                numbers={spareNumbers}
                className="spare"
                smallLayout={
                  window.innerHeight <
                  (slots.length / 10) * gridStyles.default.slotSize +
                    gridStyles.default.gridPadding * 2
                }
              />
            </DragDropContext>
          </div>
        </div>
      </Container>
    </>
  );
}

export default App;
